/**
 * Trainova AI — TenantTable Component with Search, Status Filter & Pagination
 */

import React, { useState, useMemo } from 'react';
import { Tenant } from '../types';
import { TenantStatusBadge } from './TenantStatusBadge';
import { useTranslation } from '../../../i18n';
import { Pagination } from '../../../components/ui/Pagination';

interface TenantTableProps {
  tenants: Tenant[];
  onSelectTenant: (tenant: Tenant) => void;
  onToggleStatus: (tenant: Tenant) => void;
}

export function TenantTable({ tenants, onSelectTenant, onToggleStatus }: TenantTableProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'ACTIVE' | 'SUSPENDED'>('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Filter tenants
  const filteredTenants = useMemo(() => {
    return tenants.filter((t) => {
      const matchSearch =
        searchTerm.trim() === '' ||
        (t.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.slug || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (t.id || '').toLowerCase().includes(searchTerm.toLowerCase());

      const matchStatus =
        statusFilter === 'ALL' ||
        (statusFilter === 'ACTIVE' && (t.isActive || t.status === 'ACTIVE')) ||
        (statusFilter === 'SUSPENDED' && (!t.isActive || t.status === 'SUSPENDED'));

      return matchSearch && matchStatus;
    });
  }, [tenants, searchTerm, statusFilter]);

  // Pagination calculation
  const totalItems = filteredTenants.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  
  const paginatedTenants = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredTenants.slice(startIndex, startIndex + pageSize);
  }, [filteredTenants, currentPage, pageSize]);

  // Adjust page if filters reduce item count
  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Search & Filter Bar */}
      <div
        style={{
          background: 'var(--white, #ffffff)',
          border: '1px solid var(--line, #e2e8f0)',
          borderRadius: '12px',
          padding: '14px 18px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.03)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: '260px' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: '340px' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', opacity: 0.5, fontSize: '14px' }}>
              🔍
            </span>
            <input
              type="text"
              placeholder="Search by name, slug, or ID..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                borderRadius: '8px',
                border: '1px solid var(--line, #cbd5e1)',
                fontSize: '13px',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {(['ALL', 'ACTIVE', 'SUSPENDED'] as const).map((st) => (
              <button
                key={st}
                type="button"
                onClick={() => {
                  setStatusFilter(st);
                  setCurrentPage(1);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: statusFilter === st ? '1px solid #2563eb' : '1px solid #e2e8f0',
                  background: statusFilter === st ? '#eff6ff' : '#ffffff',
                  color: statusFilter === st ? '#1d4ed8' : '#64748b',
                }}
              >
                {st === 'ALL' ? 'All' : st === 'ACTIVE' ? 'Active' : 'Suspended'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#64748b' }}>
          <span>Show:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              fontSize: '12.5px',
              background: '#ffffff',
              color: '#0f172a',
              cursor: 'pointer',
            }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
      </div>

      {/* Table Container */}
      <div className="admin-table-container" style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid var(--line, #e2e8f0)', overflowX: 'auto' }}>
        {paginatedTenants.length === 0 ? (
          <div className="admin-empty-state" style={{ padding: '48px 24px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>🏢</div>
            <p style={{ fontWeight: 600, margin: '0 0 4px', color: '#0f172a' }}>{t('table.no_orgs', undefined, 'No organizations found')}</p>
            <p style={{ fontSize: '13px', margin: 0 }}>{t('table.no_orgs_desc', undefined, 'Try modifying your search query or status filter.')}</p>
          </div>
        ) : (
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: '11.5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>{t('table.organization', undefined, 'Organization')}</th>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>{t('table.slug', undefined, 'Slug')}</th>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>{t('table.status', undefined, 'Status')}</th>
                <th style={{ padding: '12px 16px', textAlign: 'center' }}>{t('table.users', undefined, 'Users')}</th>
                <th style={{ padding: '12px 16px', textAlign: 'center' }}>{t('table.scenarios', undefined, 'Scenarios')}</th>
                <th style={{ padding: '12px 16px', textAlign: 'center' }}>{t('table.attempts', undefined, 'Attempts')}</th>
                <th style={{ padding: '12px 16px', textAlign: 'left' }}>{t('table.created', undefined, 'Created')}</th>
                <th style={{ padding: '12px 16px', textAlign: 'right' }}>{t('table.actions', undefined, 'Actions')}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedTenants.map((t) => (
                <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 16px' }}>
                    <div className="admin-tenant-cell" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div
                        className="admin-tenant-avatar"
                        style={{
                          backgroundColor: t.primaryColor || '#6366F1',
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          color: '#ffffff',
                          display: 'grid',
                          placeItems: 'center',
                          fontWeight: 700,
                          fontSize: '12px',
                        }}
                      >
                        {t.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <strong className="admin-tenant-name" style={{ color: '#0f172a', fontSize: '13.5px' }}>{t.name}</strong>
                        <div className="admin-tenant-id" style={{ fontSize: '11px', color: '#94a3b8' }}>{t.id}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <code className="admin-slug-code" style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>{t.slug}</code>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <TenantStatusBadge status={t.status} />
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>{t.userCount ?? 0}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>{t.simulationCount ?? 0}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600 }}>{t.attemptCount ?? 0}</td>
                  <td style={{ padding: '12px 16px', color: '#64748b' }}>{new Date(t.createdAt).toLocaleDateString()}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <div className="admin-table-actions" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => onSelectTenant(t)}
                        className="admin-btn admin-btn--secondary admin-btn--sm"
                        style={{
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          background: '#ffffff',
                          cursor: 'pointer',
                        }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => onToggleStatus(t)}
                        className={`admin-btn admin-btn--sm ${
                          t.isActive ? 'admin-btn--warning' : 'admin-btn--primary'
                        }`}
                        style={{
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          background: t.isActive ? '#fffbeb' : '#eff6ff',
                          color: t.isActive ? '#b45309' : '#1d4ed8',
                        }}
                      >
                        {t.isActive ? 'Suspend' : 'Activate'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Bar */}
      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setCurrentPage(1);
          }}
          showPageSizeSelector
          pageSizeOptions={[10, 20, 50, 100]}
          itemLabel="organizations"
        />
      )}
    </div>
  );
}
